import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProjectDetail from "./pages/ProjectDetail";
import Tutorials from "./pages/Tutorials";
import TutorialDetail from "./pages/TutorialDetail";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import { siteConfig } from "./config";
import LoadingScreen from "./components/LoadingScreen";
import { preloadSounds, SoundEffect } from "./lib/soundEffects";
import SoundProvider from "./components/SoundProvider";
import SoundToggleButton from "./components/SoundToggleButton";
import { initLenis, startLenis, resizeLenis, destroyLenis } from "./lib/lenis";
import { initBarba } from "./lib/barba";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

// Enhanced PreloadImages component with loading progress
const PreloadImages = ({
  onProgress,
  onComplete,
}: {
  onProgress: (progress: number) => void;
  onComplete: () => void;
}) => {
  useEffect(() => {
    // Preload all project images for smoother transitions
    const projectImages = siteConfig.projects.items.map(
      (project) => project.image
    );

    // Add critical images
    const criticalImages = [
      // Add any additional critical images here
      "/icon.png",
    ];

    const imagesToPreload = [...projectImages, ...criticalImages];

    if (imagesToPreload.length === 0) {
      onProgress(100);
      onComplete();
      return;
    }

    let loadedCount = 0;

    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        const progressValue = Math.round(
          (loadedCount / imagesToPreload.length) * 100
        );
        onProgress(progressValue);
        if (loadedCount === imagesToPreload.length) {
          onComplete();
        }
      };
      img.onerror = () => {
        // Still count errors as loaded to not block the site
        loadedCount++;
        const progressValue = Math.round(
          (loadedCount / imagesToPreload.length) * 100
        );
        onProgress(progressValue);
        if (loadedCount === imagesToPreload.length) {
          onComplete();
        }
      };
      img.src = src;
    });

    // Ensure minimum loading time for aesthetic purposes (minimum 1.5 seconds)
    setTimeout(() => {
      if (loadedCount === imagesToPreload.length) {
        onComplete();
      }
    }, 1500);

    // Debug loading progress
    console.log(`Preloading ${imagesToPreload.length} images...`);

    // Preload sound effects
    preloadSounds();
    console.log("Preloading sound effects...");
  }, [onProgress, onComplete]);

  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Don't use default scroll - Lenis will handle this
    window.scrollTo({
      top: 0,
      behavior: "auto", // Changed to auto since Lenis handles smoothness
    });
  }, [pathname]);

  return null;
};

// Lenis and Barba initialization component
const SmoothScrollProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    // Initialize Lenis
    const lenis = initLenis();
    startLenis();

    // Initialize Barba.js for page transitions
    initBarba();

    // Handle window resize
    const handleResize = () => {
      resizeLenis();
    };

    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      destroyLenis();
    };
  }, []);

  return <>{children}</>;
};

const App = () => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const handleProgress = (progress: number) => {
    setLoadingProgress(progress);
  };

  const handleComplete = () => {
    setIsLoaded(true);
    // Play a success sound when loading completes
    try {
      const audio = new Audio(`/sounds/${SoundEffect.SUCCESS}.mp3`);
      audio.volume = 0.3;
      audio.play().catch((error) => {
        console.warn("Could not play sound effect:", error);
      });
    } catch (error) {
      console.warn("Error with sound effect:", error);
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <LoadingScreen progress={loadingProgress} isLoaded={isLoaded} />
        <BrowserRouter>
          <PreloadImages
            onProgress={handleProgress}
            onComplete={handleComplete}
          />
          <ScrollToTop />
          <SmoothScrollProvider>
            <SoundProvider>
              <div
                className="application"
                id="app"
                role="application"
                data-barba="wrapper"
              >
                <div data-barba="container" data-barba-namespace="main">
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route
                      path="/project/:projectId"
                      element={<ProjectDetail />}
                    />
                    <Route path="/tutorials" element={<Tutorials />} />
                    <Route
                      path="/tutorials/:tutorialId"
                      element={<TutorialDetail />}
                    />
                    <Route
                      path="/privacy-policy"
                      element={<PrivacyPolicy />}
                    />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>
                {isLoaded && <SoundToggleButton />}
              </div>
            </SoundProvider>
          </SmoothScrollProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
